import React from 'react';
import {
  Badge, Heading, Skeleton, Stack, Text,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import GlassCard from '../GlassCard';
import LedgerTable, { LedgerColumn } from '../LedgerTable';
import useProjects from '../../hooks/useProjects';
import { Project, ProjectStatus } from '../../models/project';
import { SqlDateTimeToDisplay } from '../../utils/SqlTools';

// item 603 — a projects ledger on the Universe dashboard: every project in one searchable,
// sortable list (the shared LedgerTable, so it reads like the accountant / My Book ledgers).
// Row click opens the project. A lightweight companion to the future item-162 jobs board.
const STATUS_META: Record<number, { label: string; scheme: string }> = {
  [ProjectStatus.Open]: { label: 'Open', scheme: 'green' },
  [ProjectStatus.Proposed]: { label: 'Proposed', scheme: 'orange' },
  [ProjectStatus.Closed]: { label: 'Closed', scheme: 'gray' },
};

const statusLabel = (s: ProjectStatus) => STATUS_META[s]?.label ?? '';

const ProjectsLedgerCard: React.FC = () => {
  const history = useHistory();
  const { data, isLoading } = useProjects();
  const projects = data?.index ?? [];

  const columns: LedgerColumn<Project>[] = [
    {
      key: 'name',
      header: 'PROJECT',
      sortValue: (p) => p.name ?? '',
      render: (p) => <Text fontWeight="semibold" noOfLines={1}>{p.name}</Text>,
    },
    {
      key: 'customer',
      header: 'CUSTOMER',
      sortValue: (p) => p.customerName ?? '',
      render: (p) => <Text noOfLines={1}>{p.customerName ?? '—'}</Text>,
    },
    {
      key: 'status',
      header: 'STATUS',
      width: '120px',
      sortValue: (p) => statusLabel(p.status),
      render: (p) => {
        const m = STATUS_META[p.status];
        return m ? <Badge colorScheme={m.scheme}>{m.label}</Badge> : <Text>—</Text>;
      },
    },
    {
      key: 'created',
      header: 'CREATED',
      width: '130px',
      sortValue: (p) => p.createdDate ?? '',
      render: (p) => <Text>{SqlDateTimeToDisplay(p.createdDate)}</Text>,
    },
  ];

  return (
    <GlassCard p={{ base: 5, md: 6 }}>
      <Heading size="sm" mb={4} letterSpacing="0.06em">PROJECTS</Heading>
      {isLoading ? (
        <Stack spacing={3}>
          <Skeleton height="20px" borderRadius="md" />
          <Skeleton height="20px" borderRadius="md" />
          <Skeleton height="20px" borderRadius="md" />
        </Stack>
      ) : (
        <LedgerTable<Project>
          rows={projects}
          columns={columns}
          rowId={(p) => p.id}
          selectable={false}
          searchText={(p) => `${p.name ?? ''} ${p.customerName ?? ''} ${statusLabel(p.status)}`}
          searchPlaceholder="Search projects…"
          emptyText="No projects yet."
          onRowClick={(p) => history.push(`/project/${p.id}`)}
        />
      )}
    </GlassCard>
  );
};

export default ProjectsLedgerCard;
